import { genres as allGenres, regions as allRegions, years as allYears } from "@/mocks/filters";
import type { SortKey } from "@/utils/movie";

interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
  selectedGenres: string[];
  selectedRegions: string[];
  selectedYears: string[];
  onToggleGenre: (v: string) => void;
  onToggleRegion: (v: string) => void;
  onToggleYear: (v: string) => void;
  onReset: () => void;
  sort: SortKey;
  onSortChange: (s: SortKey) => void;
}

function ChipGroup({
  title,
  options,
  selected,
  onToggle,
}: {
  title: string;
  options: { label: string; value: string }[];
  selected: string[];
  onToggle: (v: string) => void;
}) {
  return (
    <div>
      <h4 className="mb-3 text-sm font-medium text-foreground-900">{title}</h4>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const active = selected.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onToggle(opt.value)}
              className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-xs transition-colors cursor-pointer ${
                active
                  ? "border-primary-500 bg-primary-500 text-foreground-950"
                  : "border-background-300 bg-background-100 text-foreground-700"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const SORTS: { key: SortKey; label: string }[] = [
  { key: "time", label: "按更新时间" },
  { key: "views", label: "按热度" },
  { key: "rating", label: "按评分" },
];

export default function FilterDrawer({
  open,
  onClose,
  selectedGenres,
  selectedRegions,
  selectedYears,
  onToggleGenre,
  onToggleRegion,
  onToggleYear,
  onReset,
  sort,
  onSortChange,
}: FilterDrawerProps) {
  return (
    <div
      className={`fixed inset-0 z-[70] md:hidden ${
        open ? "" : "pointer-events-none"
      }`}
    >
      <div
        className={`absolute inset-0 bg-secondary-950/70 transition-opacity ${
          open ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
      />
      <div
        className={`absolute bottom-0 left-0 right-0 max-h-[85vh] overflow-y-auto rounded-t-2xl bg-background-100 transition-transform duration-300 ${
          open ? "translate-y-0" : "translate-y-full"
        }`}
      >
        <div className="sticky top-0 flex items-center justify-between border-b border-background-300 bg-background-100 px-5 py-4">
          <h3 className="font-heading text-lg font-semibold text-foreground-950">
            筛选与排序
          </h3>
          <button
            type="button"
            aria-label="关闭"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-700 cursor-pointer"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="space-y-6 px-5 py-5">
          <ChipGroup
            title="类型"
            options={allGenres.map((g) => ({ label: g, value: g }))}
            selected={selectedGenres}
            onToggle={onToggleGenre}
          />
          <ChipGroup
            title="地区"
            options={allRegions.map((r) => ({ label: r, value: r }))}
            selected={selectedRegions}
            onToggle={onToggleRegion}
          />
          <ChipGroup
            title="年份"
            options={allYears.map((y) => ({ label: `${y}年`, value: String(y) }))}
            selected={selectedYears}
            onToggle={onToggleYear}
          />
          <ChipGroup
            title="排序方式"
            options={SORTS.map((s) => ({ label: s.label, value: s.key }))}
            selected={[sort]}
            onToggle={(v) => onSortChange(v as SortKey)}
          />
        </div>

        <div className="sticky bottom-0 flex gap-3 border-t border-background-300 bg-background-100 px-5 py-4">
          <button
            type="button"
            onClick={onReset}
            className="flex-1 whitespace-nowrap rounded-md border border-background-300 py-3 text-sm text-foreground-900 cursor-pointer"
          >
            重置筛选
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 whitespace-nowrap rounded-md bg-primary-500 py-3 text-sm font-medium text-foreground-950 cursor-pointer"
          >
            查看结果
          </button>
        </div>
      </div>
    </div>
  );
}